<script lang="ts">
export default {
  name: 'PolicyEditFormPanel',
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { Undo2 } from '@lucide/vue';
import isNumeric from 'validator/lib/isNumeric';
import { addCommas, removeCommas } from '@/utilities/Number';
import AppSelect from '@/components/v2/AppSelect.vue';
import Combobox from '@/components/v2/Combobox.vue';
import DatePickerInput from '@/components/v2/DatePickerInput.vue';
import ManagedInEpicMark from '@/components/v2/policy/ManagedInEpicMark.vue';
import type { FormSchemeType, PolicyRuleViolationFlags, ValidFormFields } from '@/composables/usePolicyEditPending';
import type { PolicySource } from '@/types/PolicySource';

interface SelectOption {
  label: string;
  value: string;
}

const props = defineProps<{
  mode: 'policy' | 'line';
  originalForm: FormSchemeType;
  validFields: ValidFormFields;
  isDisabled: boolean;
  policySource?: PolicySource;
  syndicateOptions: SelectOption[];
  carrierOptions: SelectOption[];
  wholesalerOptions?: SelectOption[];
  coverageTypeOptions?: SelectOption[];
  // Bumped by the parent to put the coverage type picker back on the form's value, e.g. when the
  // user cancels the confirmation for a change.
  coverageTypeResetKey?: number;
  validationMessages?: Partial<Record<keyof FormSchemeType, string>>;
  sideAOptions: string[];
  // Client-readiness rule flags; flagged fields get a warning border, label
  // dot, and hint until the violation is resolved (live, from current values).
  ruleViolations?: PolicyRuleViolationFlags;
}>();

const form = defineModel<FormSchemeType>('form', { required: true });

const emit = defineEmits<{
  // The coverage type is not bound to the form: a change can remove the line's sublimits and
  // subretentions, so the parent confirms it before staging it.
  (e: 'coverage-type-change', coverageType: string): void;
  (e: 'coverage-type-undo'): void;
}>();

const isLine = computed(() => props.mode === 'line');

const allowCarrierUpdate = computed(() => !!props.policySource?.AllowCarrierUpdate);
const allowPremiumUpdate = computed(() => !!props.policySource?.AllowPremiumUpdate);

// Fields the policy source doesn't let the user edit are shown read-only rather than hidden.
const premiumDisabled = computed(() => props.isDisabled || (!isLine.value && !allowPremiumUpdate.value));
const carrierDisabled = computed(() => props.isDisabled || !allowCarrierUpdate.value);
const showExternalFields = computed(() => !isLine.value && !!props.policySource);
const externalFieldsEditable = computed(() => !!props.policySource?.IsExternal);
const externalFieldsDisabled = computed(() => props.isDisabled || !externalFieldsEditable.value);

// Every source that locks these fields is fed from Epic; an asterisk on each points at the note.
const externalFieldsManagedInEpic = computed(() => showExternalFields.value && !externalFieldsEditable.value);
const premiumManagedInEpic = computed(() => showExternalFields.value && !allowPremiumUpdate.value);
const carrierManagedInEpic = computed(() => showExternalFields.value && !allowCarrierUpdate.value);
const showManagedInEpicNote = computed(() => externalFieldsManagedInEpic.value || premiumManagedInEpic.value || carrierManagedInEpic.value);

// A saved value missing from the option list (a wholesaler since deleted, or any value on a
// read-only policy, whose options aren't loaded) would otherwise render as a blank picker.
function withCurrentOption(options: SelectOption[] | undefined, current: string): SelectOption[] {
  const list = options ?? [];
  if (!current || list.some((o) => o.value === current)) return list;
  return [{ label: current, value: current }, ...list];
}

const wholesalerPickerOptions = computed(() => withCurrentOption(props.wholesalerOptions, props.originalForm.wholesaler));
const coverageTypePickerOptions = computed(() =>
  withCurrentOption(withCurrentOption(props.coverageTypeOptions, props.originalForm.coverageType), form.value.coverageType),
);

// Clearing the picker's text emits '', which is not a coverage type; remounting puts the form's
// value back in the input.
const clearedCoverageTypeCount = ref(0);
function onCoverageTypePicked(coverageType: string): void {
  if (!coverageType) {
    clearedCoverageTypeCount.value++;
    return;
  }
  if (coverageType !== form.value.coverageType) emit('coverage-type-change', coverageType);
}

const attachmentPointAmount = computed({
  get: () => (form.value.attachmentApplicable ? addCommas(form.value.attachmentPointAmount ?? '') : ''),
  set: (val: string) => {
    form.value.attachmentPointAmount = removeCommas(val)?.toString() || '';
  },
});

const aggregateLimitAmount = computed({
  get: () => (form.value.aggregateLimitApplicable ? addCommas(form.value.aggregateLimitAmount ?? '') : ''),
  set: (val: string) => {
    form.value.aggregateLimitAmount = removeCommas(val)?.toString() ?? '';
  },
});

const occurrenceLimitAmount = computed({
  get: () => (form.value.occurrenceLimitApplicable ? addCommas(form.value.occurrenceLimitAmount ?? '') : ''),
  set: (val: string) => {
    form.value.occurrenceLimitAmount = removeCommas(val)?.toString() ?? '';
  },
});

const quotaShareTotal = computed({
  get: () => addCommas(form.value.quotaShareTotal ?? ''),
  set: (val: string) => {
    form.value.quotaShareTotal = removeCommas(val)?.toString() ?? '';
  },
});

const retentionAmount = computed({
  get: () => (form.value.retentionApplicable ? addCommas(form.value.retentionAmount ?? '') : ''),
  set: (val: string) => {
    form.value.retentionAmount = removeCommas(val)?.toString() ?? '';
  },
});

const premiumTotal = computed({
  get: () => addCommas(form.value.premiumTotal ?? ''),
  set: (val: string) => {
    form.value.premiumTotal = removeCommas(val)?.toString() ?? '';
  },
});

// validFields is only refreshed on Review, so each message also checks the live value and clears
// as soon as the user fixes the field.
function amountError(field: 'quotaShareTotal' | 'premiumTotal', label: string): string {
  if (props.validFields[field]) return '';
  const value = form.value[field].trim();
  if (!value) return `${label} is required`;
  return isNumeric(value) ? '' : 'invalid amount';
}

const quotaShareError = computed(() => amountError('quotaShareTotal', 'total layer limit'));
const premiumError = computed(() => amountError('premiumTotal', 'premium total'));
const carrierMissing = computed(() => !props.validFields.carrierId && !form.value.carrierId);
const syndicateMissing = computed(() => !props.validFields.leadSyndicateId && !form.value.leadSyndicateId);

const applicableToggles = {
  attachmentPointAmount: 'attachmentApplicable',
  aggregateLimitAmount: 'aggregateLimitApplicable',
  occurrenceLimitAmount: 'occurrenceLimitApplicable',
  retentionAmount: 'retentionApplicable',
} as const;

// Turning Applicable off disables the box, so a cleared value flips it on blur rather than while
// the user is still typing. A box that loaded empty is left alone.
function markNotApplicableIfCleared(amount: keyof typeof applicableToggles): void {
  if (!form.value[amount].trim() && props.originalForm[amount].trim()) {
    form.value[applicableToggles[amount]] = false;
  }
}

function changed(field: keyof FormSchemeType): boolean {
  return form.value[field] !== props.originalForm[field];
}

const message = (field: keyof FormSchemeType, fallback: string): string => props.validationMessages?.[field] ?? fallback;

function isAmountResolved(applicable: boolean, amount: string | number | null | undefined): boolean {
  if (!applicable) return true;
  const s = amount == null ? '' : String(amount).trim();
  if (s === '') return false;
  const n = Number(s.replace(/,/g, ''));
  return Number.isFinite(n) && n > 0;
}

const showLimitViolation = computed(() => {
  if (props.ruleViolations?.noLimit !== true) return false;
  const aggResolved = isAmountResolved(form.value.aggregateLimitApplicable, form.value.aggregateLimitAmount);
  const occResolved = isAmountResolved(form.value.occurrenceLimitApplicable, form.value.occurrenceLimitAmount);
  return !(aggResolved || occResolved);
});

const showRetentionViolation = computed(() => {
  if (props.ruleViolations?.invalidRetention !== true) return false;
  return !isAmountResolved(form.value.retentionApplicable, form.value.retentionAmount);
});

const showAttachmentViolation = computed(() => {
  if (props.ruleViolations?.invalidAttachment !== true) return false;
  return !isAmountResolved(form.value.attachmentApplicable, form.value.attachmentPointAmount);
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <p
      v-if="showManagedInEpicNote"
      class="text-xs text-slate-medium"
      data-testid="policy-edit-read-only-note">
      <ManagedInEpicMark /> Managed in Epic. Corrections in Epic sync overnight.
    </p>
    <template v-if="showExternalFields">
      <div>
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Description <ManagedInEpicMark v-if="externalFieldsManagedInEpic" /></span>
        </label>
        <input
          type="text"
          v-model="form.description"
          data-testid="policy-edit-description"
          class="input input-md w-full border border-solid"
          :class="{ 'border-success': changed('description') }"
          :disabled="externalFieldsDisabled" />
      </div>
      <div class="flex gap-6">
        <div class="w-1/2">
          <label class="label">
            <span class="label-text text-xs text-slate-medium">Policy Number <ManagedInEpicMark v-if="externalFieldsManagedInEpic" /></span>
          </label>
          <input
            type="text"
            v-model="form.policyNumber"
            data-testid="policy-edit-policy-number"
            class="input input-md w-full border border-solid"
            :class="{
              'border-error': !validFields.policyNumber,
              'border-success': changed('policyNumber') && validFields.policyNumber,
            }"
            :disabled="externalFieldsDisabled" />
          <label
            v-if="!validFields.policyNumber"
            class="label">
            <span
              class="label-text-alt text-xs text-error"
              data-testid="policy-edit-policy-number-error"
              >{{ message('policyNumber', 'policy number is required') }}</span
            >
          </label>
        </div>
        <div class="w-1/2">
          <label class="label">
            <span class="label-text text-xs text-slate-medium">Wholesaler <ManagedInEpicMark v-if="externalFieldsManagedInEpic" /></span>
          </label>
          <Combobox
            v-model="form.wholesaler"
            :options="wholesalerPickerOptions"
            placeholder="Select a wholesaler..."
            :input-class="`h-10 w-full ${changed('wholesaler') ? 'border-success' : ''}`"
            :disabled="externalFieldsDisabled" />
        </div>
      </div>
      <div class="flex gap-6">
        <div class="w-1/2">
          <label class="label">
            <span class="label-text text-xs text-slate-medium">Agency <ManagedInEpicMark v-if="externalFieldsManagedInEpic" /></span>
          </label>
          <input
            type="text"
            v-model="form.agencyName"
            data-testid="policy-edit-agency"
            class="input input-md w-full border border-solid"
            :class="{ 'border-success': changed('agencyName') }"
            :disabled="externalFieldsDisabled" />
        </div>
        <div
          class="w-1/2"
          data-testid="policy-edit-coverage-type">
          <label class="label">
            <span class="label-text text-xs text-slate-medium">Coverage Type <ManagedInEpicMark v-if="externalFieldsManagedInEpic" /></span>
            <button
              v-if="changed('coverageType') && externalFieldsEditable"
              type="button"
              class="btn btn-ghost btn-xs"
              title="Undo coverage type change"
              data-testid="policy-edit-coverage-type-undo"
              :disabled="isDisabled"
              @click="emit('coverage-type-undo')">
              <Undo2 :size="14" />
            </button>
          </label>
          <Combobox
            :key="`${coverageTypeResetKey ?? 0}-${clearedCoverageTypeCount}`"
            :model-value="form.coverageType"
            :options="coverageTypePickerOptions"
            placeholder="Select a coverage type..."
            :input-class="`h-10 w-full ${!validFields.coverageType ? 'border-error' : changed('coverageType') ? 'border-success' : ''}`"
            :disabled="externalFieldsDisabled"
            @update:model-value="onCoverageTypePicked" />
          <label
            v-if="!validFields.coverageType"
            class="label">
            <span class="label-text-alt text-xs text-error">{{ message('coverageType', 'coverage type is required') }}</span>
          </label>
        </div>
      </div>
      <div class="flex gap-6">
        <div
          class="w-1/2"
          data-testid="policy-edit-effective-date">
          <label class="label">
            <span class="label-text text-xs text-slate-medium">Effective Date <ManagedInEpicMark v-if="externalFieldsManagedInEpic" /></span>
          </label>
          <DatePickerInput
            v-model="form.effectiveDate"
            :class="!validFields.effectiveDate ? 'border-error' : changed('effectiveDate') ? 'border-success' : ''"
            :disabled="externalFieldsDisabled" />
          <label
            v-if="!validFields.effectiveDate"
            class="label">
            <span class="label-text-alt text-xs text-error">{{ message('effectiveDate', 'invalid date') }}</span>
          </label>
        </div>
        <div
          class="w-1/2"
          data-testid="policy-edit-expiration-date">
          <label class="label">
            <span class="label-text text-xs text-slate-medium">Expiration Date <ManagedInEpicMark v-if="externalFieldsManagedInEpic" /></span>
          </label>
          <DatePickerInput
            v-model="form.expirationDate"
            :class="!validFields.expirationDate ? 'border-error' : changed('expirationDate') ? 'border-success' : ''"
            :disabled="externalFieldsDisabled" />
          <label
            v-if="!validFields.expirationDate"
            class="label">
            <span
              class="label-text-alt text-xs text-error"
              data-testid="policy-edit-expiration-date-error"
              >{{ message('expirationDate', 'invalid date') }}</span
            >
          </label>
        </div>
      </div>
      <!-- Epic keeps a quota share amount in this column rather than a yes/no flag. -->
      <div
        v-if="externalFieldsEditable"
        class="flex gap-6">
        <div class="w-1/2">
          <label class="label">
            <span class="label-text text-xs text-slate-medium">Quota Share</span>
          </label>
          <label class="label flex justify-normal gap-4">
            <input
              type="checkbox"
              v-model="form.isQuotaShare"
              data-testid="policy-edit-quota-share"
              class="toggle border border-solid"
              :class="{ 'border-success': changed('isQuotaShare') }"
              :disabled="isDisabled" />
          </label>
        </div>
      </div>
    </template>

    <div class="flex gap-6">
      <div
        v-if="!isLine"
        class="w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Primary / Excess</span>
        </label>
        <AppSelect
          v-model="form.peFlag"
          :options="[
            { label: 'Primary', value: 'Primary' },
            { label: 'Excess', value: 'Excess' },
            { label: 'Fees', value: 'Fees' },
          ]"
          :disabled="isDisabled"
          :class="`h-10 w-full rounded-sm ${changed('peFlag') ? 'border-success' : ''}`" />
      </div>
      <div :class="isLine ? 'w-full' : 'w-1/2'">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Attachment Point</span>
          <span
            v-if="showAttachmentViolation"
            class="h-2 w-2 rounded-full bg-warning"
            data-testid="rule-violation-dot-attachment"
            aria-hidden="true"></span>
        </label>
        <div class="flex flex-row gap-2">
          <input
            type="text"
            inputmode="numeric"
            v-model="attachmentPointAmount"
            data-testid="policy-edit-attachment"
            @blur="markNotApplicableIfCleared('attachmentPointAmount')"
            class="input input-md w-full border border-solid"
            :class="{
              'border-error': !validFields.attachmentPointAmount,
              'border-warning': showAttachmentViolation && validFields.attachmentPointAmount,
              'border-success': changed('attachmentPointAmount') && validFields.attachmentPointAmount && !showAttachmentViolation,
            }"
            :disabled="!form.attachmentApplicable || isDisabled" />
          <label class="form-control label">
            <input
              type="checkbox"
              class="toggle border border-solid border-base-300 toggle-primary"
              :class="{ 'border-success': changed('attachmentApplicable') }"
              v-model="form.attachmentApplicable"
              :disabled="isDisabled" />
            <span
              class="text-center align-top text-xs"
              :class="form.attachmentApplicable && 'text-primary'"
              >Applicable</span
            >
          </label>
        </div>
        <label
          class="label"
          :class="{ hidden: validFields.attachmentPointAmount && !showAttachmentViolation }">
          <span
            v-if="!validFields.attachmentPointAmount"
            class="label-text-alt text-xs text-error"
            >invalid amount</span
          >
          <span
            v-else-if="showAttachmentViolation"
            class="label-text-alt text-xs text-warning"
            data-testid="rule-violation-hint-attachment"
            >Violating Rule</span
          >
        </label>
      </div>
    </div>

    <div class="flex gap-6">
      <div class="w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Aggregate Limit</span>
          <span
            v-if="showLimitViolation"
            class="h-2 w-2 rounded-full bg-warning"
            data-testid="rule-violation-dot-aggregate"
            aria-hidden="true"></span>
        </label>
        <div class="flex flex-row gap-2">
          <input
            type="text"
            inputmode="numeric"
            v-model="aggregateLimitAmount"
            data-testid="policy-edit-aggregate-limit"
            @blur="markNotApplicableIfCleared('aggregateLimitAmount')"
            class="input input-md w-full border border-solid"
            :class="{
              'border-error': !validFields.aggregateLimitAmount,
              'border-warning': showLimitViolation && validFields.aggregateLimitAmount,
              'border-success': changed('aggregateLimitAmount') && validFields.aggregateLimitAmount && !showLimitViolation,
            }"
            :disabled="!form.aggregateLimitApplicable || isDisabled" />
          <label class="form-control label">
            <input
              type="checkbox"
              class="toggle border border-solid border-base-300 toggle-primary"
              :class="{ 'border-success': changed('aggregateLimitApplicable') }"
              v-model="form.aggregateLimitApplicable"
              :disabled="isDisabled" />
            <span
              class="text-center align-top text-xs"
              :class="form.aggregateLimitApplicable && 'text-primary'"
              >Applicable</span
            >
          </label>
        </div>
        <label
          class="label"
          :class="{ hidden: validFields.aggregateLimitAmount && !showLimitViolation }">
          <span
            v-if="!validFields.aggregateLimitAmount"
            class="label-text-alt text-xs text-error"
            >invalid amount</span
          >
          <span
            v-else-if="showLimitViolation"
            class="label-text-alt text-xs text-warning"
            data-testid="rule-violation-hint-aggregate"
            >Violating Rule</span
          >
        </label>
      </div>
      <div class="w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Occurrence Limit</span>
          <span
            v-if="showLimitViolation"
            class="h-2 w-2 rounded-full bg-warning"
            data-testid="rule-violation-dot-occurrence"
            aria-hidden="true"></span>
        </label>
        <div class="flex flex-row gap-2">
          <input
            type="text"
            inputmode="numeric"
            v-model="occurrenceLimitAmount"
            data-testid="policy-edit-occurrence-limit"
            @blur="markNotApplicableIfCleared('occurrenceLimitAmount')"
            class="input input-md w-full border border-solid"
            :class="{
              'border-error': !validFields.occurrenceLimitAmount,
              'border-warning': showLimitViolation && validFields.occurrenceLimitAmount,
              'border-success': changed('occurrenceLimitAmount') && validFields.occurrenceLimitAmount && !showLimitViolation,
            }"
            :disabled="!form.occurrenceLimitApplicable || isDisabled" />
          <label class="form-control label">
            <input
              type="checkbox"
              class="toggle border border-solid border-base-300 toggle-primary"
              :class="{ 'border-success': changed('occurrenceLimitApplicable') }"
              v-model="form.occurrenceLimitApplicable"
              :disabled="isDisabled" />
            <span
              class="text-center align-top text-xs"
              :class="form.occurrenceLimitApplicable && 'text-primary'"
              >Applicable</span
            >
          </label>
        </div>
        <label
          class="label"
          :class="{ hidden: validFields.occurrenceLimitAmount && !showLimitViolation }">
          <span
            v-if="!validFields.occurrenceLimitAmount"
            class="label-text-alt text-xs text-error"
            >invalid amount</span
          >
          <span
            v-else-if="showLimitViolation"
            class="label-text-alt text-xs text-warning"
            data-testid="rule-violation-hint-occurrence"
            >Violating Rule</span
          >
        </label>
      </div>
    </div>

    <div class="flex gap-6">
      <div class="w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Total Layer Limit</span>
        </label>
        <input
          type="text"
          v-model="quotaShareTotal"
          class="input input-md w-full border border-solid"
          :class="{
            'border-error': quotaShareError,
            'border-success': changed('quotaShareTotal') && !quotaShareError,
          }"
          :disabled="isDisabled" />
        <label
          v-if="quotaShareError"
          class="label">
          <span class="label-text-alt text-xs text-error">{{ quotaShareError }}</span>
        </label>
      </div>
      <div class="w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Retention</span>
          <span
            v-if="showRetentionViolation"
            class="h-2 w-2 rounded-full bg-warning"
            data-testid="rule-violation-dot-retention"
            aria-hidden="true"></span>
        </label>
        <div class="flex flex-row gap-2">
          <input
            type="text"
            v-model="retentionAmount"
            data-testid="policy-edit-retention"
            @blur="markNotApplicableIfCleared('retentionAmount')"
            class="input input-md w-full border border-solid"
            :class="{
              'border-error': !validFields.retentionAmount,
              'border-warning': showRetentionViolation && validFields.retentionAmount,
              'border-success': changed('retentionAmount') && validFields.retentionAmount && !showRetentionViolation,
            }"
            :disabled="!form.retentionApplicable || isDisabled" />
          <label class="form-control label">
            <input
              type="checkbox"
              class="toggle border border-solid border-base-300 toggle-primary"
              :class="{ 'border-success': changed('retentionApplicable') }"
              v-model="form.retentionApplicable"
              :disabled="isDisabled" />
            <span
              class="text-center align-top text-xs"
              :class="form.retentionApplicable && 'text-primary'"
              >Applicable</span
            >
          </label>
        </div>
        <label
          class="label"
          :class="{ hidden: validFields.retentionAmount && !showRetentionViolation }">
          <span
            v-if="!validFields.retentionAmount"
            class="label-text-alt text-xs text-error"
            >invalid amount</span
          >
          <span
            v-else-if="showRetentionViolation"
            class="label-text-alt text-xs text-warning"
            data-testid="rule-violation-hint-retention"
            >Violating Rule</span
          >
        </label>
      </div>
    </div>

    <div class="flex gap-6">
      <div class="w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Premium Total <ManagedInEpicMark v-if="premiumManagedInEpic" /></span>
        </label>
        <input
          type="text"
          v-model="premiumTotal"
          data-testid="policy-edit-premium"
          class="input input-md w-full border border-solid"
          :class="{
            'border-error': premiumError,
            'border-success': changed('premiumTotal') && !premiumError,
          }"
          :disabled="premiumDisabled" />
        <label
          v-if="premiumError"
          class="label">
          <span class="label-text-alt text-xs text-error">{{ premiumError }}</span>
        </label>
      </div>
      <div
        v-if="!isLine"
        class="w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Carrier <ManagedInEpicMark v-if="carrierManagedInEpic" /></span>
        </label>
        <Combobox
          v-model="form.carrierId"
          :options="carrierOptions"
          placeholder="Select a carrier..."
          :input-class="`h-10 w-full ${carrierMissing ? 'border-error' : changed('carrierId') ? 'border-success' : ''}`"
          :disabled="carrierDisabled" />
        <label
          v-if="carrierMissing"
          class="label">
          <span class="label-text-alt text-xs text-error">carrier is required</span>
        </label>
      </div>
    </div>

    <div
      v-if="!isLine"
      class="flex gap-6">
      <div class="w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Lead Syndicate</span>
        </label>
        <AppSelect
          v-model="form.leadSyndicateId"
          :options="syndicateOptions"
          :class="`h-10 w-full rounded-sm ${syndicateMissing ? 'border-error' : changed('leadSyndicateId') ? 'border-success' : ''}`"
          menu-class="w-[400px]"
          :disabled="isDisabled" />
        <label
          v-if="syndicateMissing"
          class="label">
          <span class="label-text-alt text-xs text-error">lead syndicate is required</span>
        </label>
      </div>
    </div>

    <div
      v-if="!isLine"
      class="flex gap-6">
      <div class="w-1/2">
        <div class="w-2/3">
          <label class="label">
            <span class="label-text text-xs text-slate-medium">Side A</span>
          </label>
          <AppSelect
            v-model="form.sideAValue"
            :options="sideAOptions.map((option) => ({ label: option, value: option }))"
            :class="`h-10 w-full rounded-sm ${changed('sideAValue') ? 'border-success' : ''}`"
            :disabled="isDisabled" />
        </div>
      </div>
      <div class="h-full w-1/2">
        <label class="label">
          <span class="label-text text-xs text-slate-medium">Runoff</span>
        </label>
        <label class="label flex justify-normal gap-4">
          <input
            type="checkbox"
            v-model="form.runoff"
            class="toggle border border-solid"
            :class="{ 'border-success': changed('runoff') }"
            :disabled="isDisabled" />
        </label>
      </div>
    </div>
  </div>
</template>
